import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

export function CustomCursor() {
  const [mousePosition, setMousePosition] = useState({ x: -100, y: -100 });

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const updateMousePosition = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', updateMousePosition);
    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);

  return (
    <motion.div
      className="hidden md:flex fixed top-0 left-0 w-8 h-8 rounded-full border border-brand/50 pointer-events-none z-50 items-center justify-center mix-blend-difference"
      animate={{ x: mousePosition.x - 16, y: mousePosition.y - 16 }}
      transition={{ type: 'spring', damping: 30, stiffness: 200, mass: 0.5 }}
    >
      <div className="w-1 h-1 bg-brand rounded-full" />
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                4. SVG FILTER                               */
/* -------------------------------------------------------------------------- */
export function LiquidDistortionFilter() {
  return (
    <svg className="hidden w-0 h-0">
      <filter id="liquid-distort">
        <feTurbulence type="fractalNoise" baseFrequency="0.015" numOctaves="3" result="noise">
          <animate attributeName="baseFrequency" values="0.015; 0.02; 0.015" dur="4s" repeatCount="indefinite" />
        </feTurbulence>
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="10" xChannelSelector="R" yChannelSelector="G" />
      </filter>
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/*                                5. COMPONENTS                               */

export function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: "easeOut" }}
      className="mb-12 md:mb-16 text-center lg:text-left"
    >
      {subtitle && <span className="block text-brand font-sans tracking-[0.2em] md:tracking-[0.3em] uppercase text-[10px] md:text-xs mb-4 font-bold">{subtitle}</span>}
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground font-medium tracking-tight">
        {title}
      </h2>
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                6. NEW MODULES                              */
/* -------------------------------------------------------------------------- */
export function ScrubbingText({ text }: { text: string }) {
  const containerRef = useRef<HTMLParagraphElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 85%", "end 50%"]
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0.3, 1]);
  const color = useTransform(scrollYProgress, [0, 1], ["#525252", "#ffffff"]);

  return (
    <motion.p ref={containerRef} style={{ opacity, color }} className="mb-6 text-center lg:text-left leading-relaxed">
      {text}
    </motion.p>
  );
}

